import React, { useEffect } from 'react';
import { InfoIcon } from '../components/atoms/Tooltip';

// Base reusable CounterCard component - Modern design by default
const CounterCard = ({ 
  title,
  description,
  count,
  subtitle,
  borderColor = "#E20074",
  width = 250,
  minWidth = 250, 
  extraInfo = null,
  style = {}
}) => {
  return (
    <div 
      style={{
        flex: 1,
        borderRadius: "16px",
        padding: "32px 24px",
        textAlign: "center",
        // Deep, all-sided shadow
        boxShadow: "0 10px 28px rgba(0,0,0,0.18), 0 -6px 16px rgba(0,0,0,0.06), 6px 0 14px rgba(0,0,0,0.08), -6px 0 14px rgba(0,0,0,0.08)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        minWidth: `${minWidth}px`,
        background: "#fff",
        ...style
      }}
    >
      <div style={{ 
        fontSize: "18px", 
        color: "#555", 
        marginBottom: "12px", 
        letterSpacing: "0.3px",
        fontWeight: "500"
      }}>
        {title}
        {description &&(
          <InfoIcon 
            tooltip={description}
            position='top'
            width={'250px'}
            size="18px"
            color="#64748b"
            backgroundColor="#1e293b"
            textColor="#ffffff"
            fontSize="18px"
          />
        )}
      </div>
      <div style={{ 
        fontSize: "42px", 
        fontWeight: "700", 
        color: borderColor, 
        lineHeight: "1.1",
        marginBottom: "8px"
      }}>
        {typeof count === 'number' ? count.toLocaleString() : count}
      </div>
      <div style={{ 
        fontSize: "16px", 
        color: "#666",
        fontWeight: "400"
      }}>
        {subtitle}
      </div>
      {extraInfo && (
        <div style={{
          textAlign: "center",
          fontSize: "14px",
          color: "#888",
          marginTop: "8px"
        }}>
          {extraInfo}
        </div>
      )}
    </div>
  );
};

// Reusable CounterSection component
const CounterSection = ({ 
  title, 
  children, 
  height = "auto", 
  className = "" 
}) => {
  return (
    <div className={`p-3 shadow panel-2 ${className}`} style={{ 
      borderRadius: "20px",
      maxWidth: "100%",
      margin: "20px auto",
      padding: "20px",
      height: height
    }}>
      <div style={{ 
        fontSize: "32px", 
        fontWeight: "bold", 
        marginBottom: "20px",
        color: "#e20074"
      }}>
        {title}
      </div>
      {children}
    </div>
  );
};

// Regular Day Display component
const RegularDayCounter = ({ regularDayCount, isToday }) => {
  return (
    <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <CounterCard
        title="Total Traffic"
        count={regularDayCount}
        subtitle={isToday ? "Live Count" : "Final Count"}
        width={300}
        minWidth={300}
      />
    </div>
  );
};

// Simplified Counter component (Regular Day Only)
const Counter = ({
  isToday,
  setAutoFetchEnabled,
  regularDayCount,
  counterDataAvailable,
}) => {

  useEffect(() => {
    if (isToday) {
      setAutoFetchEnabled(true);
    }
  }, [isToday, setAutoFetchEnabled]);
  
  // Render no data message if counterDataAvailable is false
  if (!counterDataAvailable) {
    return (
      <div className="container-fluid" style={{ padding: "20px" }}>
        <CounterSection 
          title=""
          height="400px"
          className="d-flex justify-content-center align-items-center"
        >
          <div className="text-center" style={{
            fontSize: "32px",
            fontWeight: "bold",
            color: "#666"
          }}>
            Counter Functionality Not Yet Enabled
            <br />
            Please Try Another Date
          </div>
        </CounterSection>
      </div>
    );
  }

  return (
    <div className="container-fluid" style={{ padding: "20px" }}>
      {/* Regular Day Traffic Section */}
      <CounterSection 
        title="Regular Day Traffic"
        height="400px"
      >
        <RegularDayCounter 
          regularDayCount={regularDayCount} 
          isToday={isToday} 
        />
      </CounterSection>
    </div>
  );
}

export default React.memo(Counter, (prevProps, nextProps) => {
  return (
    prevProps.regularDayCount === nextProps.regularDayCount &&
    prevProps.isToday === nextProps.isToday &&
    prevProps.counterDataAvailable === nextProps.counterDataAvailable
  );
});

export { CounterCard, CounterSection }